import styled, { css } from 'styled-components';
import Markdown from 'react-markdown';
import { always, equals, path } from 'ramda';

import { CARD_SHADOW_COLOR, WHITE } from '../../utils/colors';
import { media } from '../../utils/media';
import { propCond } from '../../utils/propCond';
import { ZINDEX } from '../../utils/constants';
import { ReactComponent as CloseIconComponent } from '../../images/close-icon.svg';

const getThemeColor = path(['theme', 'color']);

export const Link = styled.a`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.17em;
  letter-spacing: 0.6px;
  text-decoration: none;

  &:after {
    content: '>';
    display: inline-block;
    margin-left: 5px;
    line-height: 1em;
    position: relative;
    top: -1px;
  }
`;

export const ContentMarkdown = styled(Markdown)`
  h3 {
    color: ${getThemeColor};
    font-size: 16px;
    line-height: 1.4em;
    font-weight: 500;
    font-family: 'FreightText';
  }
`;

export const triangleLeftAfterStyles = css`
  --size: 18px;
  content: '▲';
  position: absolute;
  text-shadow: 0 -3px 7px #cfcfcf;
  width: var(--size);
  height: var(--size);
  font-size: var(--size);
  line-height: var(--size);
  color: ${WHITE};

  transform: rotate(-90deg);
  left: calc(-1 * var(--size) + 4px);
  top: calc(50% - var(--size) / 2 - 4px);
`;

export const Card = styled.div`
  font-size: 14px;
  font-weight: 300;
  line-height: 1.5em;
  padding: 24px 20px 20px 20px;
  margin: 10px 0;
  box-shadow: 0 2px 7px 0 ${CARD_SHADOW_COLOR};
  background-color: ${WHITE};
  border-top: 2px solid ${getThemeColor};
  position: relative;

  &:first-child::after {
    ${triangleLeftAfterStyles};
    top: 8px;
  }

  > p {
    margin-bottom: 0;
  }

  ${Link} {
    display: block;
    margin: 20px 0 12px 0;
    text-transform: uppercase;
    font-weight: bold;
    color: ${getThemeColor};
  }

  ${media.desktop`
    &:first-child::after {
      content: none;
    }
  `}

  @media print {
    &:first-child::after {
      content: none;
    }
  }
`;

export const ExitButton = styled.button`
  background: none;
  border: none;
  position: absolute;
  right: 5px;
  top: 5px;

  svg g {
    stroke: ${getThemeColor};
  }

  ${media.desktop`
    display: none;
  `}

  @media print {
    display: none;
  }
`;

const condVisibility = propCond('isVisible');

export const CardContainer = styled.div`
  position: absolute;
  left: 60px;
  top: -10px;
  width: calc(100vw - 70px);
  max-width: 100%;
  transition: opacity 300ms, transform 300ms;
  z-index: ${ZINDEX.CARD_LEFT};
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;

  > * {
    flex-basis: 240px;
    margin: 10px 10px 10px 0;
  }

  ${condVisibility([
    [
      equals(true),
      always(css`
        opacity: 1;
        transform: scale(1);
      `),
    ],
    [
      equals(false),
      always(css`
        opacity: 0;
        transform: scale(0.9);
        pointer-events: none;
      `),
    ],
  ])};

  ${media.desktop`
    position: static;
    opacity: 1;
    transform: none;
    pointer-events: unset;
  `}

  @media print {
    position: static;
    opacity: 1;
    transform: none;
    pointer-events: unset;
  }
`;

export const CardToggleButton = styled.button`
  border-radius: 100%;
  margin-top: 5px;
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  border: none;
  color: ${WHITE};
  background-color: ${getThemeColor};
  transition: transform 0.3s;

  :focus {
    outline: none;
  }

  ${media.desktop`
    display: none;
    max-width: 100%;
  `}

  @media print {
    display: none;
    max-width: 100%;
  }

  ${condVisibility([
    [
      equals(true),
      always(css`
        transform: rotate(45deg);
      `),
    ],
  ])}
`;

export const CardToggleCloseIcon = styled(CloseIconComponent)`
  transform: rotate(45deg);
  width: 11px;
  height: 11px;

  path {
    stroke: ${WHITE};
    stroke-width: 2;
  }
`;
